/**
 * /dashboard/travel-preapprovals — G15 Travel Pre-approvals hub.
 *
 * Two tabs:
 *   - Mine     — caller's own pre-approvals (always available).
 *   - Approvals — pre-approvals awaiting the caller's decision (visible
 *                 only when isApprover, i.e. manager / hr_manager /
 *                 company_admin — mirror of the server's role-filtered
 *                 SQL scope; the backend remains the boundary).
 *
 * Top-right CTA → /dashboard/travel-preapprovals/new.
 *
 * URL state in ?tab=mine|approvals (zod-validated, defaults via role).
 * Mirrors dashboard.offboarding.index.tsx's tab-in-URL pattern (Pattern
 * A consolidation) so a refresh / share preserves the view.
 *
 * Replaces the previous <ComingSoon> stub at this URL (G15 frontend).
 */

import { useCallback } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
 useParams,} from "@tanstack/react-router";
import { z } from "zod";
import { Inbox, PlaneTakeoff, Plus, User } from "lucide-react";
import { PageHeader } from "@/components/shared/page-header";
import { TravelPreapprovalList } from "@/components/travel/travel-preapproval-list";
import { useTravelRoles } from "@/components/travel/use-travel-roles";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

type TravelTab = "mine" | "approvals";

const travelSearchSchema = z.object({
  tab: z.enum(["mine", "approvals"]).optional(),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/travel-preapprovals/",
)({
  validateSearch: travelSearchSchema,
  component: TravelPreapprovalsHubRoute,
});

function isTravelTab(v: string): v is TravelTab {
  return v === "mine" || v === "approvals";
}

function TravelPreapprovalsHubRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { isApprover } = useTravelRoles();
  const search = Route.useSearch();
  const navigate = useNavigate();

  // Default tab: approver lands on the approvals queue (their work);
  // everyone else lands on their own list.
  const defaultTab: TravelTab = isApprover ? "approvals" : "mine";
  const requested = search.tab;
  // If a non-approver hits ?tab=approvals (deep-link from before they
  // had the role, etc.), silently fall back to "mine" — the list
  // component already 403-shapes any out-of-scope read, but the tab
  // strip should not advertise a capability they don't have.
  const activeTab: TravelTab =
    requested === "approvals" && !isApprover
      ? "mine"
      : (requested ?? defaultTab);

  const setTab = useCallback(
    (value: string) => {
      const next: TravelTab = isTravelTab(value) ? value : defaultTab;
      void navigate({
        to: "/app/$tenantSlug/travel-preapprovals", params: { tenantSlug },
        search: { tab: next },
        replace: true,
      });
    },
    [navigate, defaultTab],
  );

  const description = isApprover
    ? "Review pre-trip budget requests, and manage your own."
    : "Request manager / HR approval for a trip before you book it.";

  return (
    <>
      <PageHeader
        title="Travel pre-approvals"
        description={description}
        actions={
          <Button asChild className="gap-1.5">
            <Link to="/app/$tenantSlug/travel-preapprovals/new" params={{ tenantSlug }}>
              <Plus className="h-4 w-4" />
              New travel pre-approval
            </Link>
          </Button>
        }
      />

      {isApprover ? (
        <Tabs value={activeTab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="mine" className="gap-1.5">
              <User className="h-4 w-4" />
              Mine
            </TabsTrigger>
            <TabsTrigger value="approvals" className="gap-1.5">
              <Inbox className="h-4 w-4" />
              Approvals
            </TabsTrigger>
          </TabsList>
          <TabsContent value="mine">
            <TravelPreapprovalList mode="self" />
          </TabsContent>
          <TabsContent value="approvals">
            <TravelPreapprovalList mode="approver" />
          </TabsContent>
        </Tabs>
      ) : (
        // Non-approver: skip the tab strip entirely — they only ever
        // see their own list.
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground sm:hidden">
            <PlaneTakeoff aria-hidden className="h-4 w-4" />
            <span>My pre-approvals</span>
          </div>
          <TravelPreapprovalList mode="self" />
        </div>
      )}
    </>
  );
}
